import React, { ReactNode } from "react";
import { IntlShape, MessageDescriptor, useIntl } from "react-intl";
import { msgs } from "../../msgs";
import {
  ChainUpdateEntry,
  PromotionChangeEntry,
  PromotionChanges,
  TranslationChangeEntry,
  UpdatePromotionInput,
  WindowRange,
} from "../../types";
import { ApprovalCard, CardHeader, ChangeRow, MetaLabel, Section, StatePill } from "./shared";
import {
  OTHER_CHANGE_LABELS,
  WINDOW_FIELDS,
  WithWindowFields,
  describeInventoryScope,
  formatChangeValue,
  formatTranslationContent,
  inventoryChangeLabel,
  inventoryScopeCountLabel,
  isAllInventoryPhrase,
  resolveDiscountScope,
  windowsForEntry,
} from "./formatting";
import { useLocaleName } from "./locale-hooks";
import {
  DeviceTypesValue,
  GeolocationUpdateValue,
  WindowRangesView,
  restrictionSentence,
} from "./restriction-views";

// One row inside the unified "Restricciones" section (min/max stay, min/max notice) — null when
// the field wasn't touched at all; "—" when the restriction was cleared (after === null);
// otherwise the bolded restriction sentence with the RESULTING value (shown even when it already
// matches the current value — see the "always show the resulting value" note in PromotionChangeRows).
function RestrictionRow({
  labelMsg,
  diff,
  unit,
  intl,
}: {
  labelMsg: MessageDescriptor;
  diff: PromotionChangeEntry<number | null> | undefined;
  unit: "nights" | "days";
  intl: IntlShape;
}): React.JSX.Element | null {
  if (diff == null) return null;
  if (diff.after == null) {
    return (
      <span className="text-[#212121] text-sm break-words">{intl.formatMessage(labelMsg)}: —</span>
    );
  }
  return (
    <span className="text-[#212121] text-sm break-words">
      {restrictionSentence(intl, labelMsg, diff.after, unit)}
    </span>
  );
}

// Visibility (isVisible) renders as the same rounded-pill "badge" the create card uses for its
// "Estado al crearse" pills (StatePill — see PromotionApprovalSummary's "No visible" pill), not
// as plain text, so a visibility change reads identically whether it came from creating or
// updating a promotion. `fa-eye`/`fa-eye-slash` mirror the visible/hidden state, same convention
// as the create card's icon choices.
function VisibilityBadge({
  value,
  intl,
}: {
  value: unknown;
  intl: IntlShape;
}): React.JSX.Element | null {
  if (typeof value !== "boolean") return null;
  return value ? (
    <StatePill icon="fa-eye">{intl.formatMessage(msgs.approvalStateVisible)}</StatePill>
  ) : (
    <StatePill icon="fa-eye-slash">{intl.formatMessage(msgs.approvalStateNotVisible)}</StatePill>
  );
}

/**
 * Chain-update-only per-child inventory breakdown, shown inside "Descuento y alcance" (or the
 * standalone inventory-scope Section when there's no rate change) INSTEAD of a flat line count —
 * same collapsible-dropdown affordance as the create card's chain breakdown, populated from
 * `inventoryByChild` (see ChainUpdateEntry) instead of `CreatePromotionInput.inventoryByChild`.
 * Only ever rendered when `inventoryByChild` is non-empty — i.e. this entry actually requested an
 * inventory change AND is a chain (aggregator) update, never for a single-property update.
 */
export function ChainInventoryByChildDropdown({
  inventoryByChild,
  intl,
}: {
  inventoryByChild: NonNullable<ChainUpdateEntry["inventoryByChild"]>;
  intl: IntlShape;
}) {
  const totalLines = inventoryByChild.reduce(
    (sum, child) => sum + (typeof child?.lineCount === "number" ? child.lineCount : 0),
    0
  );
  // The <summary> text is the ONLY part of this dropdown visible without expanding it — same
  // ambiguity as everywhere else (see inventoryChangeLabel's doc comment), it matters MOST here,
  // since a user who never expands the dropdown would otherwise only ever see the plain "Applies
  // to 0 inventory lines" even when every child's per-row text (inside, collapsed by default)
  // already says "Removes N". Aggregate to a mode-aware summary ONLY when every child agrees on
  // the SAME mode and carries a `beforeCount` — mixing modes (or a legacy entry with neither) has
  // no single coherent phrasing, so it falls back to the plain total, unchanged from before.
  const modes = new Set(inventoryByChild.map(child => child?.mode).filter(Boolean));
  const uniformMode = modes.size === 1 ? inventoryByChild[0]?.mode : undefined;
  const everyChildHasBeforeCount = inventoryByChild.every(
    child => typeof child?.beforeCount === "number"
  );
  const totalBeforeCount = everyChildHasBeforeCount
    ? inventoryByChild.reduce((sum, child) => sum + (child!.beforeCount ?? 0), 0)
    : undefined;
  // `inventoryChangeLabel` now takes the already-computed `affectedCount` directly (see its own
  // doc comment) rather than deriving a delta from beforeCount/afterCount itself — compute that
  // delta HERE instead, same condition as before (uniform mode + every child carrying a
  // beforeCount), falling back to the plain total (mode: undefined) exactly like before this
  // change whenever that condition isn't met.
  const canComputeChainDelta =
    typeof totalBeforeCount === "number" && (uniformMode === "add" || uniformMode === "remove");
  const summaryAffectedCount = canComputeChainDelta
    ? uniformMode === "remove"
      ? Math.max(totalBeforeCount! - totalLines, 0)
      : Math.max(totalLines - totalBeforeCount!, 0)
    : totalLines;
  const summaryLabel = inventoryChangeLabel(
    { mode: canComputeChainDelta ? uniformMode : undefined, affectedCount: summaryAffectedCount },
    intl
  );
  return (
    <details className="group w-full rounded-[10px] border border-[#E5E7EB] bg-white overflow-hidden">
      <summary className="flex h-[43px] items-center justify-between gap-2 px-4 cursor-pointer list-none text-[#212121] text-sm marker:content-none [&::-webkit-details-marker]:hidden">
        {summaryLabel}
        <em className="fa fa-chevron-down text-[13px] text-[#212121]" aria-hidden />
      </summary>
      <div className="flex flex-col gap-2 max-h-[168px] overflow-y-auto px-4 pb-3">
        {inventoryByChild.map((child, ci) => {
          const lineCount = typeof child?.lineCount === "number" ? child.lineCount : 0;
          const ticker = child?.childTicker ?? "—";
          // Same ambiguity as the single-property card (see inventoryChangeLabel's doc comment):
          // a bare per-child count can't tell "removed everything for this engine" (or "added N
          // for this engine") apart from a no-op. Only shown as "Removes N (M left)" / "Adds N (M
          // total)" when this SPECIFIC child's mode/beforeCount are both present — a child resolved
          // through the legacy scalar-only fallbacks (no mode/beforeCount attached) still gets the
          // plain count, same as before this change.
          const hasBeforeCount = typeof child?.beforeCount === "number";
          const removed =
            child?.mode === "remove" && hasBeforeCount
              ? Math.max(child!.beforeCount! - lineCount, 0)
              : 0;
          const added =
            child?.mode === "add" && hasBeforeCount
              ? Math.max(lineCount - child!.beforeCount!, 0)
              : 0;
          return (
            <div key={ticker} className="flex flex-col gap-0.5 min-w-0">
              <span className="text-[#212121] text-sm break-all">
                {removed > 0
                  ? intl.formatMessage(msgs.approvalInventoryChildLinesRemoved, {
                      ticker,
                      removed,
                      remaining: lineCount,
                    })
                  : added > 0
                  ? intl.formatMessage(msgs.approvalInventoryChildLinesAdded, {
                      ticker,
                      added,
                      total: lineCount,
                    })
                  : intl.formatMessage(msgs.approvalInventoryChildLines, {
                      ticker,
                      count: lineCount,
                    })}
              </span>
            </div>
          );
        })}
      </div>
    </details>
  );
}

/**
 * Renders every changed attribute of ONE promotion entry (windows, scalar/array fields,
 * geolocation, translations) as a stack of individual Section rows — shared by
 * MultiUpdateApprovalSummary and MultiUpdateChainApprovalSummary so a multi-update approval is
 * organized PER PROMOTION (one card listing everything that changes for it) instead of per
 * FIELD (one Section per field, repeating every promotion's name once per changed field — the
 * layout this replaced). A field whose requested value already matches the promotion's current
 * one (a no-op) still gets its OWN row here, with "Sin cambios" as its value, instead of being
 * silently dropped or bucketed into a separate section elsewhere in the card — that previously
 * left a ticker looking like it both changed (in one Section) and didn't (in "Sin cambios"),
 * which is confusing once a promotion has more than one requested field.
 *
 * Each field is its own `Section`, rendered as a DIRECT child of the caller's `ApprovalCard` (one
 * per promotion — see MultiUpdateApprovalSummary/MultiUpdateChainApprovalSummary), so it picks up
 * the same `[&>*]:border-b` row dividers the single-promotion UpdateApprovalSummary card uses —
 * matching that card's visual language field-for-field instead of the old single bundled block.
 */
export function PromotionChangeRows({
  entry,
  intl,
}: {
  entry: WithWindowFields;
  intl: IntlShape;
}): React.JSX.Element {
  const changes = entry.changes;
  const dash = (v: string) => (v == null || v === "" ? "—" : v);
  const localeName = useLocaleName();

  // Every field the request touched is shown with its RESULTING value — never "Sin cambios" —
  // even when that value already matches the promotion's current one (a field only reaches
  // `changes` because the user asked to set it, so showing the value it will be left at is the
  // point of the approval card). This keeps every field coherent with the date sections, which
  // always rendered their value regardless.
  const rateDiff = changes?.rate;
  const discountScope = rateDiff != null ? resolveDiscountScope(entry, intl) : null;
  // Specific legend ("Habitaciones Dobles") in place of the generic "las tarifas seleccionadas"
  // fallback baked into discountScope.scopeLabel — see WithWindowFields.inventoryScopeDescriptor's
  // doc comment. null (not just absent) whenever the descriptor doesn't apply (all inventory, no
  // inventory change, nothing specific matched), so `?? discountScope?.scopeLabel` below always
  // falls back correctly, same pattern as create-summary.tsx.
  const specificScope = describeInventoryScope(entry.inventoryScopeDescriptor, intl);
  // Chain update ONLY (see ChainUpdateEntry.inventoryByChild): when present, this entry actually
  // requested an inventory change on an aggregator-managed promotion — show the per-child
  // breakdown dropdown instead of a flat line count, gated the same way the create card gates its
  // own chain dropdown (non-empty inventoryByChild = chain). Never present for a single-property
  // update, which has no `inventoryByChild` field at all.
  const inventoryByChild = (Array.isArray(entry.inventoryByChild)
    ? entry.inventoryByChild
    : []
  ).filter(c => c != null);
  const hasChainInventoryBreakdown = inventoryByChild.length > 0;
  const hasRestrictionChanges =
    changes?.minStay != null ||
    changes?.maxStay != null ||
    changes?.minNotice != null ||
    changes?.maxNotice != null;

  return (
    <>
      {(Object.keys(WINDOW_FIELDS) as (keyof typeof WINDOW_FIELDS)[]).map(key => {
        const windows = windowsForEntry(entry, key);
        if (windows.length === 0) return null;
        return (
          <Section key={key} label={intl.formatMessage(WINDOW_FIELDS[key].label)}>
            <WindowRangesView windows={windows} />
          </Section>
        );
      })}
      {/* Descuento y alcance — same section/labels as the create card, showing the rate change
          together with the affected inventory-line count instead of a bare "Tarifa" text row. */}
      {rateDiff != null && (
        <Section label={intl.formatMessage(msgs.approvalSectionDiscount)}>
          {specificScope ?? discountScope?.scopeLabel ? (
            <div className="text-[#212121] text-sm">
              {intl.formatMessage(msgs.approvalDiscountScope, {
                rate: rateDiff.after,
                scope: specificScope ?? discountScope!.scopeLabel!,
                b: (chunks: ReactNode) => <strong className="font-bold">{chunks}</strong>,
              })}
            </div>
          ) : (
            <div className="text-[#212121] text-sm">
              <strong className="font-bold">{rateDiff.after}</strong>
            </div>
          )}
          {hasChainInventoryBreakdown ? (
            <ChainInventoryByChildDropdown inventoryByChild={inventoryByChild} intl={intl} />
          ) : (
            discountScope?.lineCountCaption && (
              <span className="text-[#949494] text-[14px] font-normal leading-none">
                {discountScope.lineCountCaption}
              </span>
            )
          )}
        </Section>
      )}
      {/* Repetible — GIVE_NIGHT only: whether the discount cycle repeats within the same stay. */}
      {changes?.giveNightRepeatable != null && (
        <Section label={intl.formatMessage(msgs.approvalGiveNightRepeatable)}>
          <span className="text-[#212121] text-sm break-words">
            {intl.formatMessage(
              changes.giveNightRepeatable.after ? msgs.confirmYes : msgs.confirmNo
            )}
          </span>
        </Section>
      )}
      {/* Inventory shown on its own only when the discount section above didn't already cover it
          (a rate change always shows the resulting scope inline with it). Rendered whenever there
          is a per-child breakdown (chain, from entries[].inventoryByChild OR childInventoryOverrides)
          OR the entry carried an inventory-scope change — no longer gated on a rate change, so the
          per-child line counts are never dropped just because inventory was the only thing changed. */}
      {rateDiff == null && (hasChainInventoryBreakdown || changes?.inventoryScope != null) && (
        <Section label={intl.formatMessage(msgs.approvalInventoryScope)}>
          {hasChainInventoryBreakdown ? (
            <ChainInventoryByChildDropdown inventoryByChild={inventoryByChild} intl={intl} />
          ) : (
            <span className="text-[#212121] text-sm break-words">
              {(() => {
                const scope = changes!.inventoryScope!;
                const allLabel = intl.formatMessage(msgs.approvalAllInventory);
                const afterValue = String(scope.after);
                // Always show the REAL affected-line count (see `affectedCount`'s doc comment in
                // types.ts) — including "all inventory", which now shows its real resolved total
                // instead of just repeating the plain phrase label with no number. Falls back to
                // the legacy comma-count heuristic only for entries that predate `affectedCount`.
                if (typeof scope.affectedCount !== "number") {
                  return isAllInventoryPhrase(afterValue, allLabel)
                    ? allLabel
                    : inventoryScopeCountLabel(afterValue, allLabel, intl, scope.afterCount);
                }
                return inventoryChangeLabel(
                  { mode: scope.operationMode, affectedCount: scope.affectedCount },
                  intl
                );
              })()}
            </span>
          )}
        </Section>
      )}
      {changes?.deviceTypes != null && (
        <Section label={intl.formatMessage(msgs.approvalDeviceTypes)}>
          <DeviceTypesValue
            devices={Array.isArray(changes.deviceTypes.after) ? changes.deviceTypes.after : []}
            intl={intl}
          />
        </Section>
      )}
      {/* Restricciones — min/max stay AND min/max advance notice unified under one section, each
          rendered as its own bolded "<label> de <value>" sentence (see RestrictionRow). */}
      {hasRestrictionChanges && (
        <Section label={intl.formatMessage(msgs.approvalRestrictionsTitle)}>
          <div className="flex flex-col gap-1.5">
            <RestrictionRow
              labelMsg={msgs.approvalMinStay}
              diff={changes?.minStay}
              unit="nights"
              intl={intl}
            />
            <RestrictionRow
              labelMsg={msgs.approvalMaxStay}
              diff={changes?.maxStay}
              unit="nights"
              intl={intl}
            />
            <RestrictionRow
              labelMsg={msgs.approvalMinNotice}
              diff={changes?.minNotice}
              unit="days"
              intl={intl}
            />
            <RestrictionRow
              labelMsg={msgs.approvalMaxNotice}
              diff={changes?.maxNotice}
              unit="days"
              intl={intl}
            />
          </div>
        </Section>
      )}
      {(Object.keys(OTHER_CHANGE_LABELS) as (keyof typeof OTHER_CHANGE_LABELS)[]).map(key => {
        const diff = changes?.[key];
        if (diff == null) return null;
        return (
          <Section key={key} label={intl.formatMessage(OTHER_CHANGE_LABELS[key])}>
            {key === "isVisible" ? (
              // Section's row is a column flex container, which would otherwise stretch the
              // badge to the row's full width (flex default align-items: stretch) — self-start
              // keeps it sized to its content, same natural pill width as the create card's
              // "Estado al crearse" pills (rendered in a row flex-wrap container instead, so
              // they never had this problem).
              <div className="self-start">
                <VisibilityBadge value={diff.after} intl={intl} />
              </div>
            ) : (
              <span className="text-[#212121] text-sm break-words">
                {formatChangeValue(key, diff.after, dash)}
              </span>
            )}
          </Section>
        );
      })}
      {changes?.geolocation != null && (
        <Section label={intl.formatMessage(msgs.approvalGeolocationTitle)}>
          <GeolocationUpdateValue descriptor={changes.geolocation.after} intl={intl} />
        </Section>
      )}
      {Array.isArray(changes?.translations) && changes.translations.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalTranslations)}>
          <div className="flex flex-col gap-2">
            {changes.translations.map(t => (
              <div key={t.locale} className="flex flex-col gap-0.5 min-w-0">
                <span className="font-bold text-[#212121] text-sm">{localeName(t.locale)}</span>
                <span className="text-[#212121] text-sm break-words">
                  {formatTranslationContent(t.after)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

/** Renders the before→after diff carried in the updatePromotion approval input. */
export function UpdateApprovalSummary({ input }: { input: unknown }) {
  const intl = useIntl();
  const localeName = useLocaleName();
  const data = (input ?? {}) as UpdatePromotionInput;
  // The backend's `changes` diff is a KEYED OBJECT (see PromotionChanges in types.ts) —
  // only the keys that actually changed are present. This replaced an earlier flat array
  // shape; keep this in sync with update-promotion.dto.ts's `changes` zod schema.
  const changes: PromotionChanges = data.changes ?? {};
  // Promo code(s) the backend sends as `codes`; rendered joined so the user can read them.
  const codes = (Array.isArray(data.codes) ? data.codes : []).filter(c => c != null && c !== "");

  // Resulting date windows after the update, rendered the same way as the create card (a
  // labelled section with each range on its own line, see PromotionApprovalSummary) instead
  // of a plain before/after text line. Each kind normally arrives on its OWN flat field
  // (openToTravelWindows, etc.); fall back to `changes.<key>.after` so the dates still
  // render identically to the create card even if only the diff carries them.
  const windowsByKey = (Object.keys(WINDOW_FIELDS) as (keyof typeof WINDOW_FIELDS)[]).reduce(
    (acc, key) => {
      const flat = data[WINDOW_FIELDS[key].flatField];
      acc[key] = Array.isArray(flat) && flat.length > 0 ? flat : changes[key]?.after ?? [];
      return acc;
    },
    {} as Record<keyof typeof WINDOW_FIELDS, WindowRange[]>
  );
  const hasWindows = Object.values(windowsByKey).some(w => w.length > 0);

  // `translations` is an ARRAY of per-locale entries (see `TranslationChangeEntry` in
  // types.ts), not a scalar before/after pair — one row per changed locale, `before: null`
  // for a newly-added one. Rendered separately from the generic ChangeRow loop below.
  const translationChanges: TranslationChangeEntry[] = (Array.isArray(changes.translations)
    ? changes.translations
    : []
  ).filter(t => t != null);

  const dash = (v: string) => (v == null || v === "" ? "—" : v);

  // Every other (non-window) changed field, rendered as a plain before→after row.
  // `inventoryScope` is special-cased: the backend sends free text (the "all inventory" phrase
  // or a comma-separated list of line names), and we only ever want to show the total line
  // COUNT before/after, never the raw list — same reasoning as the create card, which shows a
  // count instead of enumerating lines.
  const allInventoryLabel = intl.formatMessage(msgs.approvalAllInventory);
  type ChangeRowData = { key: string; label: string; before: string; after: string };
  // Visibility is a boolean flip (isVisible: false → true), not a scalar/list value — it
  // needs its own "Visible"/"No visible" labels (same wording as the create card's status
  // pills and WidgetObject's state display) instead of formatChangeValue's generic
  // String(value), which would otherwise render the literal "true"/"false".
  const visibleLabel = intl.formatMessage(msgs.approvalStateVisible);
  const notVisibleLabel = intl.formatMessage(msgs.approvalStateNotVisible);
  const formatVisibility = (value: unknown): string =>
    typeof value === "boolean" ? (value ? visibleLabel : notVisibleLabel) : dash("");

  const otherChanges: ChangeRowData[] = (Object.keys(
    OTHER_CHANGE_LABELS
  ) as (keyof typeof OTHER_CHANGE_LABELS)[])
    // Every field the request touched is shown with its RESULTING value — no longer filtered out
    // when the requested value already matches the current one ("always show the resulting value").
    // `codes` is excluded here ONLY when the dedicated "Código promocional" block above already
    // rendered it (codes.length > 0) — otherwise the promo code would render twice in the same
    // card. When codes.length === 0 (e.g. the code was cleared to null/empty), that block doesn't
    // render at all, so this generic row is left as the only place the change is visible.
    .filter(key => (key !== "codes" || codes.length === 0) && changes[key] != null)
    .map(key => ({
      key,
      label: intl.formatMessage(OTHER_CHANGE_LABELS[key]),
      before:
        key === "isVisible"
          ? formatVisibility(changes[key]!.before)
          : formatChangeValue(key, changes[key]!.before, dash),
      after:
        key === "isVisible"
          ? formatVisibility(changes[key]!.after)
          : formatChangeValue(key, changes[key]!.after, dash),
    }));

  // Descuento y alcance — same section/labels as the create card (reused via
  // resolveDiscountScope), showing the rate change together with the affected inventory-line
  // count instead of the old bare "Tarifa" text row. Whenever the update touches the rate at all,
  // this section takes over showing the resulting scope too, so the standalone inventory-scope
  // row below is skipped (see `showInventoryScopeAlone`) to avoid showing the same scope twice.
  const rateDiff = changes.rate;
  const discountScope =
    rateDiff != null
      ? resolveDiscountScope(
          {
            changes,
            appliesToAllInventory: data.appliesToAllInventory,
            appliesToBookable: data.appliesToBookable,
          },
          intl
        )
      : null;
  // Specific legend ("Habitaciones Dobles") in place of the generic "las tarifas seleccionadas"
  // fallback baked into discountScope.scopeLabel — see UpdatePromotionInput.inventoryScopeDescriptor's
  // doc comment. null whenever it doesn't apply (all inventory, no inventory change, nothing
  // specific matched), so `?? discountScope?.scopeLabel` below always falls back correctly, same
  // pattern as create-summary.tsx and PromotionChangeRows above.
  const specificScope = describeInventoryScope(data.inventoryScopeDescriptor, intl);

  // Inventory scope: prefer the `changes.inventoryScope` diff (it carries a real "before"), but
  // fall back to the flat resulting fields (`appliesToAllInventory`/`appliesToBookable`) — same
  // pattern as `windowsByKey` above — so a scope change is never silently dropped from the card
  // just because the diff omitted or mis-copied `inventoryScope` (the "before" is unknown in that
  // case, so it renders as "—" rather than inventing one). Skipped entirely when the discount
  // section above already shows the resulting scope (rateDiff != null).
  const inventoryScopeDiff = changes.inventoryScope;
  const showInventoryScopeAlone =
    rateDiff == null &&
    (inventoryScopeDiff != null ||
      data.appliesToAllInventory ||
      (Array.isArray(data.appliesToBookable) && data.appliesToBookable.length > 0));
  if (showInventoryScopeAlone) {
    if (inventoryScopeDiff != null) {
      otherChanges.push({
        key: "inventoryScope",
        label: intl.formatMessage(msgs.approvalInventoryScope),
        before: inventoryScopeCountLabel(
          String(inventoryScopeDiff.before),
          allInventoryLabel,
          intl,
          inventoryScopeDiff.beforeCount
        ),
        after:
          typeof inventoryScopeDiff.affectedCount === "number"
            ? inventoryChangeLabel(
                {
                  mode: inventoryScopeDiff.operationMode,
                  affectedCount: inventoryScopeDiff.affectedCount,
                },
                intl
              )
            : isAllInventoryPhrase(String(inventoryScopeDiff.after), allInventoryLabel)
            ? allInventoryLabel
            : inventoryScopeCountLabel(
                String(inventoryScopeDiff.after),
                allInventoryLabel,
                intl,
                inventoryScopeDiff.afterCount
              ),
      });
    } else {
      const after = data.appliesToAllInventory
        ? allInventoryLabel
        : inventoryScopeCountLabel(data.appliesToBookable!.join(", "), allInventoryLabel, intl);
      otherChanges.push({
        key: "inventoryScope",
        label: intl.formatMessage(msgs.approvalInventoryScope),
        before: "—",
        after,
      });
    }
  }

  // Device types — special-cased (like geolocation) since it renders via the collapsible
  // DeviceTypesValue component, not a plain before→after text row.
  const deviceTypesDiff = changes.deviceTypes;
  const showDeviceTypes = deviceTypesDiff != null;

  // Restrictions — min/max stay AND min/max advance notice unified under one section (see
  // RestrictionRow), instead of one generic ChangeRow per field.
  const hasRestrictionChanges =
    changes.minStay != null ||
    changes.maxStay != null ||
    changes.minNotice != null ||
    changes.maxNotice != null;

  // Geolocation: structured {mode, countries} descriptor — special-cased like inventoryScope
  // above since it can't go through the generic formatChangeValue path (see OTHER_CHANGE_LABELS),
  // and rendered via GeolocationUpdateValue (below) instead of a plain string so the country list
  // always shows the "Afecta a X países incluidos/excluidos" legend + collapsible dropdown.
  // `before` is always deterministically derived server-side (describeGeolocationMode), so it
  // never needs a fallback the way inventoryScope's does; it isn't rendered either way.
  const geolocationDiff = changes.geolocation;
  const showGeolocation = geolocationDiff != null;

  // Nothing to display: a diff, assigned promo code(s), resulting date windows, and a
  // translation change are ALL absent. This happens when the requested value is the same one
  // the promotion already has (e.g. "set the rate to 10%" when it's already 10%) — every entry
  // above is filtered out as a no-op, so there is nothing left to show. Rather than silently
  // rendering an empty card (which left the user unable to tell why nothing appeared), say so
  // explicitly.
  if (
    otherChanges.length === 0 &&
    !showGeolocation &&
    rateDiff == null &&
    changes.giveNightRepeatable == null &&
    !showDeviceTypes &&
    !hasRestrictionChanges &&
    codes.length === 0 &&
    !hasWindows &&
    translationChanges.length === 0
  ) {
    return (
      <ApprovalCard>
        {data.ticker && <CardHeader title={data.ticker} />}
        <div className="pl-3 pr-4 py-3 bg-white">
          <span className="text-[#6A7896] text-sm">
            {intl.formatMessage(msgs.approvalNoChangesMessage)}
          </span>
        </div>
      </ApprovalCard>
    );
  }

  return (
    <ApprovalCard>
      {data.ticker && <CardHeader title={data.ticker} />}
      {codes.length > 0 && (
        <div className="flex flex-col gap-0.5 min-w-0 pl-3 pr-4 py-3 bg-white">
          <MetaLabel>{intl.formatMessage(msgs.approvalPromoCode)}</MetaLabel>
          <span className="text-[#212121] font-medium truncate">{codes.join(", ")}</span>
        </div>
      )}
      {(Object.keys(WINDOW_FIELDS) as (keyof typeof WINDOW_FIELDS)[]).map(
        key =>
          windowsByKey[key].length > 0 && (
            <Section key={key} label={intl.formatMessage(WINDOW_FIELDS[key].label)}>
              <WindowRangesView windows={windowsByKey[key]} />
            </Section>
          )
      )}
      {rateDiff != null && (
        <Section label={intl.formatMessage(msgs.approvalSectionDiscount)}>
          {specificScope ?? discountScope?.scopeLabel ? (
            <div className="text-[#212121] text-sm">
              {intl.formatMessage(msgs.approvalDiscountScope, {
                rate: rateDiff.after,
                scope: specificScope ?? discountScope!.scopeLabel!,
                b: (chunks: ReactNode) => <strong className="font-bold">{chunks}</strong>,
              })}
            </div>
          ) : (
            <div className="text-[#212121] text-sm">
              <strong className="font-bold">{rateDiff.after}</strong>
            </div>
          )}
          {discountScope?.lineCountCaption && (
            <span className="text-[#949494] text-[14px] font-normal leading-none">
              {discountScope.lineCountCaption}
            </span>
          )}
        </Section>
      )}
      {/* Repetible — GIVE_NIGHT only: whether the discount cycle repeats within the same stay. */}
      {changes.giveNightRepeatable != null && (
        <Section label={intl.formatMessage(msgs.approvalGiveNightRepeatable)}>
          <span className="text-[#212121] text-sm break-words">
            {intl.formatMessage(
              changes.giveNightRepeatable.after ? msgs.confirmYes : msgs.confirmNo
            )}
          </span>
        </Section>
      )}
      {showDeviceTypes && (
        <ChangeRow
          field={intl.formatMessage(msgs.approvalDeviceTypes)}
          before={null}
          after={
            <DeviceTypesValue
              devices={Array.isArray(deviceTypesDiff!.after) ? deviceTypesDiff!.after : []}
              intl={intl}
            />
          }
        />
      )}
      {hasRestrictionChanges && (
        <Section label={intl.formatMessage(msgs.approvalRestrictionsTitle)}>
          <div className="flex flex-col gap-1.5">
            <RestrictionRow
              labelMsg={msgs.approvalMinStay}
              diff={changes.minStay}
              unit="nights"
              intl={intl}
            />
            <RestrictionRow
              labelMsg={msgs.approvalMaxStay}
              diff={changes.maxStay}
              unit="nights"
              intl={intl}
            />
            <RestrictionRow
              labelMsg={msgs.approvalMinNotice}
              diff={changes.minNotice}
              unit="days"
              intl={intl}
            />
            <RestrictionRow
              labelMsg={msgs.approvalMaxNotice}
              diff={changes.maxNotice}
              unit="days"
              intl={intl}
            />
          </div>
        </Section>
      )}
      {otherChanges.map(c => (
        <ChangeRow key={c.key} field={c.label} before={c.before} after={c.after} />
      ))}
      {showGeolocation && (
        <ChangeRow
          field={intl.formatMessage(msgs.approvalGeolocationTitle)}
          before={null}
          after={<GeolocationUpdateValue descriptor={geolocationDiff!.after} intl={intl} />}
        />
      )}
      {translationChanges.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalTranslations)}>
          <div className="flex flex-col gap-2">
            {translationChanges.map(t => (
              <div key={t.locale} className="flex flex-col gap-0.5 min-w-0">
                <span className="font-bold text-[#212121] text-sm">{localeName(t.locale)}</span>
                {/* Pending PO spec: only the new ("after") content is shown for now, not the
                    original — see ChangeRow above for the same rule on the other fields. */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[#212121] text-sm break-words">
                    {formatTranslationContent(t.after)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </ApprovalCard>
  );
}
